import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import loanGivenService from '../../services/LoanGivenService.js';
import axiosInstance from '../../config/axios.js';
import {
  HandCoins,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Check,
  X,
  Unlock,
  DollarSign,
  Receipt,
} from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const LoanGivenList = () => {
  const [loans, setLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: '',
    borrower_type: '',
    status: '',
    user_id: '',
    overdue_only: false,
  });
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchLoans(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchLoans(currentPage);
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true&status=active');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchLoans = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.borrower_type) filters.borrower_type = filterValues.borrower_type;
      if (filterValues.user_id) filters.user_id = filterValues.user_id;
      if (filterValues.search) filters.search = filterValues.search;
      if (filterValues.overdue_only) filters.overdue_only = 'true';

      const response = await loanGivenService.getAll(page, perPage, filters);
      setLoans(response.data || []);
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load loan records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(`Approve loan for "${borrowerName}"?`, 'Approve Loan');
    if (confirmed) {
      try {
        await loanGivenService.approve(loanId);
        Alert.success('Loan approved successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async (loanId, borrowerName) => {
    const reason = await Alert.prompt(`Enter rejection reason for "${borrowerName}":`, 'Reject Loan');
    if (reason !== null) {
      try {
        await loanGivenService.reject(loanId, reason || 'No reason provided');
        Alert.success('Loan rejected successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject loan');
      }
    }
  };

  const handleActivate = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(`Activate loan for "${borrowerName}"?`, 'Activate Loan');
    if (confirmed) {
      try {
        await loanGivenService.activate(loanId);
        Alert.success('Loan activated successfully!');
        fetchLoans();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async (loanId, borrowerName) => {
    const confirmed = await Alert.confirm(`Delete loan record for "${borrowerName}"?`, 'Delete Loan');
    if (confirmed) {
      try {
        await loanGivenService.delete(loanId);
        Alert.success('Loan deleted successfully!');
        if (loans.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchLoans();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const clearFilters = () => {
    setFilterValues({
      search: '',
      borrower_type: '',
      status: '',
      user_id: '',
      overdue_only: false,
    });
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'loan_id',
      label: 'Loan ID',
      render: (loan) => (
        <button
          type="button"
          onClick={() => navigate(`/loans-given/view/${loan.loan_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {loan.loan_id}
        </button>
      ),
    },
    {
      key: 'borrower',
      label: 'Borrower',
      render: (loan) => (
        <div className="text-sm">
          <div className="font-medium">{loan.borrower_name}</div>
          {loan.borrower_company && <div className="text-xs text-gray-500">{loan.borrower_company}</div>}
          {loan.user_details && <div className="text-xs text-gray-400">{loan.user_details.user_id}</div>}
        </div>
      ),
    },
    {
      key: 'borrower_type',
      label: 'Type',
      render: (loan) => (
        <StatusBadge
          status={loan.borrower_type}
          label={loan.borrower_type.replace(/_/g, ' ').toUpperCase()}
        />
      ),
    },
    {
      key: 'loan_amount',
      label: 'Amount',
      render: (loan) => formatCurrency(loan.loan_amount),
    },
    {
      key: 'amount_pending',
      label: 'Pending',
      cellClassName: 'text-red-600 font-semibold',
      render: (loan) => formatCurrency(loan.amount_pending),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (loan) => (
        <div>
          <div>{formatDate(loan.due_date)}</div>
          {loan.is_overdue && <span className="text-xs text-red-600 font-semibold">OVERDUE</span>}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (loan) => <StatusBadge status={loan.status} />,
    },
  ];

  const getActions = (loan) => {
    const actions = [
      {
        label: 'View Details',
        icon: Eye,
        onClick: () => navigate(`/loans-given/view/${loan.loan_id}`),
      },
    ];

    if (loan.status === 'pending') {
      actions.push(
        { label: 'Approve', icon: Check, onClick: () => handleApprove(loan.loan_id, loan.borrower_name) },
        { label: 'Reject', icon: X, variant: 'danger', onClick: () => handleReject(loan.loan_id, loan.borrower_name) }
      );
    }

    if (loan.status === 'approved') {
      actions.push({
        label: 'Activate',
        icon: Unlock,
        onClick: () => handleActivate(loan.loan_id, loan.borrower_name),
      });
    }

    if (loan.status === 'approved' || loan.status === 'active') {
      actions.push({
        label: 'Add Repayment',
        icon: DollarSign,
        onClick: () => navigate(`/loans-given/repayment/${loan.loan_id}`),
      });
    }

    if (!['completed', 'rejected'].includes(loan.status)) {
      actions.push({
        label: 'Edit',
        icon: Pencil,
        onClick: () => navigate(`/loans-given/edit/${loan.loan_id}`),
      });
    }

    if (['pending', 'rejected'].includes(loan.status)) {
      actions.push({
        label: 'Delete',
        icon: Trash2,
        variant: 'danger',
        onClick: () => handleDelete(loan.loan_id, loan.borrower_name),
      });
    }

    return actions;
  };

  return (
    <PageLayout
      title="Expenses & Loans"
      icon={HandCoins}
      actions={
        <>
          <Button icon={Plus} onClick={() => navigate('/loans-given/add')}>
            Add Loan
          </Button>
          <Button variant="outline" icon={Receipt} onClick={() => navigate('/expenses/add')}>
            Add Expenses
          </Button>
        </>
      }
    >
      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Loan ID, Name, Company...' },
          {
            key: 'borrower_type',
            label: 'Borrower Type',
            type: 'select',
            options: [
              { value: 'employee', label: 'Employee' },
              { value: 'external_company', label: 'External Company' },
              { value: 'individual', label: 'Individual' },
            ],
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'defaulted', label: 'Defaulted' },
            ],
          },
          {
            key: 'user_id',
            label: 'Employee',
            type: 'select',
            options: users.map((user) => ({
              value: user.user_id,
              label: `${user.name} - ${user.user_id}`,
            })),
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={clearFilters}
      />

      <label className="flex items-center gap-2 text-sm mb-4 px-1">
        <input
          type="checkbox"
          checked={filterValues.overdue_only}
          onChange={(e) => setFilterValues((prev) => ({ ...prev, overdue_only: e.target.checked }))}
          className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
        />
        <span className="text-gray-700">Show Overdue Only</span>
      </label>

      <DataTable
        columns={columns}
        data={loans}
        loading={loading}
        emptyMessage="No loan records found"
        rowKey="loan_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default LoanGivenList;
